import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

const TYPES = [
  'appel_sortant','appel_entrant','email','rdv','visite','salon','autre'
];
const TYPE_LABELS = {
  appel_sortant: '📞 Appel sortant',
  appel_entrant: '📲 Appel entrant',
  email:         '✉ Email',
  rdv:           '📅 RDV',
  visite:        '🚗 Visite',
  salon:         '🏛 Salon',
  autre:         '💬 Autre',
};
const NIVEAU_LABELS = ['','❄ Très froid','🌤 Froid','🌡 Tiède','🔥 Chaud','⚡ Très chaud'];

function ModalInteraction({ inter, clients, canaux, onSave, onClose }) {
  const isEdit = !!inter?.id;
  const [form, setForm] = useState({
    client_id:          inter?.client_id          || '',
    type_interaction:   inter?.type_interaction   || 'appel_sortant',
    canal_source_id:    inter?.canal_source_id    || '',
    date_interaction:   inter?.date_interaction?.slice(0,16) || new Date().toISOString().slice(0,16),
    duree_minutes:      inter?.duree_minutes      || '',
    objet:              inter?.objet              || '',
    contenu:            inter?.contenu            || '',
    niveau_interet:     inter?.niveau_interet     || '',
    suite_a_donner:     inter?.suite_a_donner     || '',
    date_suite:         inter?.date_suite?.slice(0,10) || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  async function handleSave() {
    if (!form.client_id) return setError('Client requis');
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        duree_minutes:   form.duree_minutes ? parseInt(form.duree_minutes) : null,
        niveau_interet:  form.niveau_interet ? parseInt(form.niveau_interet) : null,
        canal_source_id: form.canal_source_id || null,
        date_suite:      form.date_suite || null,
      };
      if (isEdit) {
        await apiFetch(`/crm/interactions/${inter.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/crm/interactions', { method: 'POST', body: JSON.stringify(body) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Modifier l\'interaction' : 'Nouvelle interaction'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-grid-2">
            <div>
              <label className="form-label">Client *</label>
              <select style={inputStyle} value={form.client_id}
                onChange={e => set('client_id', e.target.value)}>
                <option value="">Sélectionner…</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Type</label>
              <select style={inputStyle} value={form.type_interaction}
                onChange={e => set('type_interaction', e.target.value)}>
                {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Date et heure</label>
              <input type="datetime-local" style={inputStyle}
                value={form.date_interaction}
                onChange={e => set('date_interaction', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Durée (minutes)</label>
              <input type="number" min="0" style={inputStyle}
                value={form.duree_minutes}
                onChange={e => set('duree_minutes', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Objet</label>
              <input style={inputStyle} value={form.objet}
                onChange={e => set('objet', e.target.value)}
                placeholder="Résumé en une ligne" />
            </div>
            <div>
              <label className="form-label">Niveau d'intérêt</label>
              <select style={inputStyle} value={form.niveau_interet || ''}
                onChange={e => set('niveau_interet', e.target.value)}>
                <option value="">—</option>
                {[1,2,3,4,5].map(n => (
                  <option key={n} value={n}>{NIVEAU_LABELS[n]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Canal</label>
              <select style={inputStyle} value={form.canal_source_id || ''}
                onChange={e => set('canal_source_id', e.target.value)}>
                <option value="">—</option>
                {canaux.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes / compte-rendu</label>
              <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }}
                value={form.contenu} onChange={e => set('contenu', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Suite à donner</label>
              <input style={inputStyle} value={form.suite_a_donner}
                onChange={e => set('suite_a_donner', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Date de relance</label>
              <input type="date" style={inputStyle} value={form.date_suite}
                onChange={e => set('date_suite', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CRMInteractions() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [clients, setClients]   = useState([]);
  const [canaux, setCanaux]     = useState([]);
  const [filter, setFilter]     = useState({ client_id: '', type_interaction: '', date_debut: '', date_fin: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);

  const LIMIT = 30;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    apiFetch(`/crm/interactions?${params}`)
      .then(r => { setItems(r.data); setTotal(r.total); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/crm/clients?limit=500').then(r => setClients(r.data || [])).catch(() => {});
    apiFetch('/crm/canaux-source').then(r => setCanaux(r.data || [])).catch(() => {});
  }, []);

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette interaction ?')) return;
    try {
      await apiFetch(`/crm/interactions/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  const setF = (k, v) => setFilter(f => ({ ...f, [k]: v }));

  return (
    <div className="crm-section-page">
      <div className="crm-toolbar">
        <div className="crm-filters">
          <select className="crm-select" value={filter.client_id}
            onChange={e => { setF('client_id', e.target.value); setPage(1); }}>
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
          </select>
          <select className="crm-select" value={filter.type_interaction}
            onChange={e => { setF('type_interaction', e.target.value); setPage(1); }}>
            <option value="">Tous les types</option>
            {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
          <input type="date" className="crm-select" value={filter.date_debut}
            onChange={e => { setF('date_debut', e.target.value); setPage(1); }}
            title="Depuis" />
          <input type="date" className="crm-select" value={filter.date_fin}
            onChange={e => { setF('date_fin', e.target.value); setPage(1); }}
            title="Jusqu'au" />
        </div>
        <button className="btn btn-primary crm-btn-sm"
          onClick={() => { setEditing(null); setShowModal(true); }}>
          + Interaction
        </button>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="crm-interactions-list">
            {items.length === 0 && <div className="crm-empty">Aucune interaction trouvée</div>}
            {items.map(it => (
              <div key={it.id} className="crm-interaction-item">
                <div className="crm-interaction-icon">
                  {TYPE_LABELS[it.type_interaction]?.split(' ')[0] || '💬'}
                </div>
                <div className="crm-interaction-body">
                  <div className="crm-interaction-header">
                    <span className="crm-interaction-type">{TYPE_LABELS[it.type_interaction] || it.type_interaction}</span>
                    <span className="crm-interaction-client">{it.nom_client}</span>
                    {it.niveau_interet && (
                      <span className="crm-interaction-niveau">{NIVEAU_LABELS[it.niveau_interet]}</span>
                    )}
                    <span className="crm-interaction-date">
                      {new Date(it.date_interaction).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                  {it.objet && <div className="crm-interaction-objet">{it.objet}</div>}
                  {it.contenu && <div className="crm-interaction-contenu">{it.contenu}</div>}
                  {it.suite_a_donner && (
                    <div className="crm-interaction-suite">
                      📌 {it.suite_a_donner}
                      {it.date_suite && ` — avant le ${new Date(it.date_suite).toLocaleDateString('fr-FR')}`}
                    </div>
                  )}
                  <div className="crm-interaction-footer">
                    <span className="crm-text-sm">{it.utilisateur_nom || ''}</span>
                    {it.duree_minutes && <span className="crm-text-sm">{it.duree_minutes} min</span>}
                  </div>
                </div>
                <div className="crm-interaction-actions">
                  <button className="crm-btn-edit" onClick={() => { setEditing(it); setShowModal(true); }}>✎</button>
                  <button className="crm-btn-edit crm-card-btn--danger" onClick={() => handleDelete(it.id)}>✕</button>
                </div>
              </div>
            ))}
          </div>

          {total > LIMIT && (
            <div className="crm-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span>Page {page} / {Math.ceil(total / LIMIT)}</span>
              <button disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
          <div className="crm-count">{total} interaction{total > 1 ? 's' : ''}</div>
        </>
      )}

      {showModal && (
        <ModalInteraction
          inter={editing}
          clients={clients}
          canaux={canaux}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
